import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import config from 'config'
import api from 'utils/api'
import { buildUrl } from 'utils/helpers'
import { showErrorMessage } from 'store/ui/actions'

export const fetchKeyLists = createAsyncThunk(
  'addressLists/fetchKeyLists',
  async ({ page = 1, perPage = 25 } = {}, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        buildUrl('/entity_lists', {
          list_kind: 'key',
          page,
          per_page: perPage,
        }), // eslint-disable-line camelcase
        {},
        config.baseUri
      )
      return { ...payload, page }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchAvailableMasterKeys = createAsyncThunk(
  'addressLists/fetchAvailableMasterKeys',
  async (
    { search = '', excludeEntityListId = null, page = 1, perPage = 25 } = {},
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = await api.request(
        buildUrl('/entity_lists/available_master_keys', {
          search: search || undefined,
          exclude_entity_list_id: excludeEntityListId || undefined, // eslint-disable-line camelcase
          page,
          per_page: perPage, // eslint-disable-line camelcase
        }),
        {},
        config.baseUri
      )
      return {
        data: payload.data || [],
        meta: payload.metadata.meta || {},
        page,
      }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchMasterKeysByEntityList = createAsyncThunk(
  'addressLists/fetchMasterKeysByEntityList',
  async (
    { search = '', entityListId = null, page = 1, perPage = 25 } = {},
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = await api.request(
        buildUrl('/entity_lists/available_master_keys', {
          search: search || undefined,
          entity_list_id: entityListId, // eslint-disable-line camelcase
          page,
          per_page: perPage, // eslint-disable-line camelcase
        }),
        {},
        config.baseUri
      )
      return {
        data: payload.data || [],
        meta: payload.metadata.meta || {},
        page,
      }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchAllMasterKeysByEntityList = createAsyncThunk(
  'addressLists/fetchAllMasterKeysByEntityList',
  async ({ entityListId }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        buildUrl('/entity_lists/available_master_keys', {
          entity_list_id: entityListId, // eslint-disable-line camelcase
          page: 1,
          per_page: 1000, // eslint-disable-line camelcase
        }),
        {},
        config.baseUri
      )
      return { data: payload.data || [] }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const createKeyList = createAsyncThunk(
  'addressLists/createKeyList',
  async ({ name }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        '/entity_lists',
        {
          method: 'POST',
          body: JSON.stringify({ entity_list: { name, kind: 'key' } }),
        },
        config.baseUri
      )
      return payload.data?.entity_list
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const updateKeyList = createAsyncThunk(
  'addressLists/updateKeyList',
  async ({ id, name }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        `/entity_lists/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ entity_list: { name } }),
        },
        config.baseUri
      )
      return payload.data?.entity_list
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const syncKeyListMembers = createAsyncThunk(
  'addressLists/syncKeyListMembers',
  async ({ id, masterKeyUuids }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        `/entity_lists/${id}/sync_master_keys`,
        {
          method: 'PATCH',
          body: JSON.stringify({ master_key_uuids: masterKeyUuids }), // eslint-disable-line camelcase
        },
        config.baseUri
      )
      return payload
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchAddressLists = createAsyncThunk(
  'addressLists/fetch',
  async ({ page = 1, perPage = 25 } = {}, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        buildUrl('/entity_lists', {
          list_kind: 'address',
          page,
          per_page: perPage,
        }), // eslint-disable-line camelcase
        {},
        config.baseUri
      )
      return { ...payload, page }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchAddressListsForKey = createAsyncThunk(
  'addressLists/fetchForKey',
  async (
    {
      search = '',
      keyUuid = null,
      excludeKeyUuid = null,
      page = 1,
      perPage = 25,
    } = {},
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = await api.request(
        buildUrl('/entity_lists', {
          display_kind: 'key', // eslint-disable-line camelcase
          search: search || undefined,
          member_kind: keyUuid || excludeKeyUuid ? 'master_key' : undefined, // eslint-disable-line camelcase
          member_uuid: keyUuid || undefined, // eslint-disable-line camelcase
          exclude_member_uuid: excludeKeyUuid || undefined, // eslint-disable-line camelcase
          kind: 'address',
          page,
          per_page: perPage, // eslint-disable-line camelcase
        }),
        {},
        config.baseUri
      )
      return { ...payload, page }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchKeyListsForKey = createAsyncThunk(
  'keyLists/fetchForKey',
  async (
    {
      search = '',
      keyUuid = null,
      excludeKeyUuid = null,
      page = 1,
      perPage = 25,
    } = {},
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = await api.request(
        buildUrl('/entity_lists', {
          display_kind: 'key', // eslint-disable-line camelcase
          kind: 'key',
          list_kind: 'key', // eslint-disable-line camelcase
          search: search || undefined,
          elem_kind: keyUuid || excludeKeyUuid ? 'master_key' : undefined, // eslint-disable-line camelcase
          elem_uuid: keyUuid || undefined, // eslint-disable-line camelcase
          exclude_elem_uuid: excludeKeyUuid || undefined, // eslint-disable-line camelcase
          page,
          per_page: perPage, // eslint-disable-line camelcase
        }),
        {},
        config.baseUri
      )
      return { ...payload, page }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchEntityList = createAsyncThunk(
  'addressLists/fetchOne',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        `/entity_lists/${id}`,
        {},
        config.baseUri
      )
      return payload.data?.entity_list
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchAvailableEntries = createAsyncThunk(
  'addressLists/fetchAvailableEntries',
  async (
    { search = '', excludeEntityListId = null, page = 1, perPage = 10 } = {},
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = await api.request(
        buildUrl('/entity_lists/available_entries', {
          search: search || undefined,
          exclude_entity_list_id: excludeEntityListId || undefined, // eslint-disable-line camelcase
          page,
          per_page: perPage, // eslint-disable-line camelcase
        }),
        {},
        config.baseUri
      )
      return {
        data: payload.data || [],
        meta: payload.metadata.meta || {},
        page,
      }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchEntriesByEntityList = createAsyncThunk(
  'addressLists/fetchEntriesByEntityList',
  async (
    { search = '', entityListId = null, page = 1, perPage = 25 } = {},
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = await api.request(
        buildUrl('/entity_lists/available_entries', {
          search: search || undefined,
          entity_list_id: entityListId, // eslint-disable-line camelcase
          page,
          per_page: perPage, // eslint-disable-line camelcase
        }),
        {},
        config.baseUri
      )
      return {
        data: payload.data || [],
        meta: payload.metadata.meta || {},
        page,
      }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchAllEntriesByEntityList = createAsyncThunk(
  'addressLists/fetchAllEntriesByEntityList',
  async ({ entityListId }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        buildUrl('/entity_lists/available_entries', {
          entity_list_id: entityListId, // eslint-disable-line camelcase
          page: 1,
          per_page: 1000, // eslint-disable-line camelcase
        }),
        {},
        config.baseUri
      )
      return { data: payload.data || [] }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const deleteEntityList = createAsyncThunk(
  'addressLists/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        `/entity_lists/${id}`,
        { method: 'DELETE' },
        config.baseUri
      )
      return payload
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const createEntityList = createAsyncThunk(
  'addressLists/create',
  async ({ name }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        '/entity_lists',
        {
          method: 'POST',
          body: JSON.stringify({ entity_list: { name } }),
        },
        config.baseUri
      )
      return payload.data?.entity_list
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const updateEntityList = createAsyncThunk(
  'addressLists/update',
  async ({ id, name }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        `/entity_lists/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ entity_list: { name } }),
        },
        config.baseUri
      )
      return payload.data?.entity_list
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const syncEntityListEntries = createAsyncThunk(
  'addressLists/syncEntries',
  async ({ id, entryUuids }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        `/entity_lists/${id}/sync_entries`,
        {
          method: 'PATCH',
          body: JSON.stringify({ entry_uuids: entryUuids }), // eslint-disable-line camelcase
        },
        config.baseUri
      )
      return payload
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

const initialEntriesMeta = { total: 0, page: 1, perPage: 10, totalPages: 1 }
const initialKeysMeta = { total: 0, page: 1, perPage: 25, totalPages: 1 }
const initialListsMeta = { total: 0, page: 1, perPage: 25, totalPages: 1 }

const initialState = {
  list: [],
  meta: { total: 0, page: 1, perPage: 25, totalPages: 1 },
  pending: false,
  keyLists: [],
  keyListsMeta: { total: 0, page: 1, perPage: 25, totalPages: 1 },
  pendingKeyLists: false,
  availableAddressListsForKey: [],
  availableAddressListsForKeyMeta: initialListsMeta,
  pendingAvailableAddressListsForKey: false,
  addressListsForKey: [],
  addressListsForKeyMeta: initialListsMeta,
  pendingAddressListsForKey: false,
  availableKeyListsForKey: [],
  availableKeyListsForKeyMeta: initialListsMeta,
  pendingAvailableKeyListsForKey: false,
  keyListsForKey: [],
  keyListsForKeyMeta: initialListsMeta,
  pendingKeyListsForKey: false,
  availableEntries: [],
  availableEntriesMeta: initialEntriesMeta,
  pendingEntries: false,
  entriesByEntityList: [],
  entriesByEntityListMeta: initialEntriesMeta,
  pendingEntriesByEntityList: false,
  allEntriesByEntityList: [],
  pendingAllEntriesByEntityList: false,
  availableMasterKeys: [],
  availableMasterKeysMeta: initialKeysMeta,
  pendingMasterKeys: false,
  masterKeysByEntityList: [],
  masterKeysByEntityListMeta: initialKeysMeta,
  pendingMasterKeysByEntityList: false,
  allMasterKeysByEntityList: [],
  pendingAllMasterKeysByEntityList: false,
  pendingMasterKeySync: false,
  currentList: null,
  pendingList: false,
  pendingEntrySync: false,
  pendingDelete: false,
}

const addressListsSlice = createSlice({
  name: 'addressLists',
  initialState,
  reducers: {
    clearAddressLists: () => initialState,
    clearKeyLists: (state) => {
      state.keyLists = []
      state.keyListsMeta = initialState.keyListsMeta
      state.pendingKeyLists = false
    },
    clearAddressListsForKey: (state) => {
      state.availableAddressListsForKey = []
      state.availableAddressListsForKeyMeta = initialListsMeta
      state.pendingAvailableAddressListsForKey = false
      state.addressListsForKey = []
      state.addressListsForKeyMeta = initialListsMeta
      state.pendingAddressListsForKey = false
    },
    clearKeyListsForKey: (state) => {
      state.availableKeyListsForKey = []
      state.availableKeyListsForKeyMeta = initialListsMeta
      state.pendingAvailableKeyListsForKey = false
      state.keyListsForKey = []
      state.keyListsForKeyMeta = initialListsMeta
      state.pendingKeyListsForKey = false
    },
    clearAvailableEntries: (state) => {
      state.availableEntries = []
      state.availableEntriesMeta = initialEntriesMeta
      state.pendingEntries = false
    },
    clearEntriesByEntityList: (state) => {
      state.entriesByEntityList = []
      state.entriesByEntityListMeta = initialEntriesMeta
      state.pendingEntriesByEntityList = false
    },
    clearAllEntriesByEntityList: (state) => {
      state.allEntriesByEntityList = []
      state.pendingAllEntriesByEntityList = false
    },
    clearAvailableMasterKeys: (state) => {
      state.availableMasterKeys = []
      state.availableMasterKeysMeta = initialKeysMeta
      state.pendingMasterKeys = false
    },
    clearMasterKeysByEntityList: (state) => {
      state.masterKeysByEntityList = []
      state.masterKeysByEntityListMeta = initialKeysMeta
      state.pendingMasterKeysByEntityList = false
    },
    clearAllMasterKeysByEntityList: (state) => {
      state.allMasterKeysByEntityList = []
      state.pendingAllMasterKeysByEntityList = false
    },
    clearCurrentList: (state) => {
      state.currentList = null
      state.pendingList = false
    },
  },
  extraReducers: {
    [fetchAddressLists.pending]: (state) => {
      state.pending = true
    },
    [fetchAddressLists.fulfilled]: (state, { payload }) => {
      state.pending = false
      state.list = payload.data || []
      const {
        total_count: total = 0,
        page = 1,
        per_page: perPage = 25,
        total_pages: totalPages = 1,
      } = payload.metadata?.meta || {}
      state.meta = { total, page, perPage, totalPages }
    },
    [fetchAddressLists.rejected]: (state) => {
      state.pending = false
    },
    [fetchKeyListsForKey.pending]: (state, { meta }) => {
      if (meta.arg?.keyUuid) state.pendingKeyListsForKey = true
      else state.pendingAvailableKeyListsForKey = true
    },
    [fetchKeyListsForKey.fulfilled]: (state, { payload, meta }) => {
      const {
        total_count: total = 0,
        page = 1,
        per_page: perPage = 25,
        total_pages: totalPages = 1,
      } = payload.metadata?.meta || {}
      if (meta.arg?.keyUuid) {
        state.pendingKeyListsForKey = false
        state.keyListsForKey = payload.data || []
        state.keyListsForKeyMeta = { total, page, perPage, totalPages }
      } else {
        state.pendingAvailableKeyListsForKey = false
        state.availableKeyListsForKey = payload.data || []
        state.availableKeyListsForKeyMeta = { total, page, perPage, totalPages }
      }
    },
    [fetchKeyListsForKey.rejected]: (state, { meta }) => {
      if (meta.arg?.keyUuid) state.pendingKeyListsForKey = false
      else state.pendingAvailableKeyListsForKey = false
    },
    [fetchAddressListsForKey.pending]: (state, { meta }) => {
      if (meta.arg?.keyUuid) state.pendingAddressListsForKey = true
      else state.pendingAvailableAddressListsForKey = true
    },
    [fetchAddressListsForKey.fulfilled]: (state, { payload, meta }) => {
      const {
        total_count: total = 0,
        page = 1,
        per_page: perPage = 25,
        total_pages: totalPages = 1,
      } = payload.metadata?.meta || {}
      if (meta.arg?.keyUuid) {
        state.pendingAddressListsForKey = false
        state.addressListsForKey = payload.data || []
        state.addressListsForKeyMeta = { total, page, perPage, totalPages }
      } else {
        state.pendingAvailableAddressListsForKey = false
        state.availableAddressListsForKey = payload.data || []
        state.availableAddressListsForKeyMeta = {
          total,
          page,
          perPage,
          totalPages,
        }
      }
    },
    [fetchAddressListsForKey.rejected]: (state, { meta }) => {
      if (meta.arg?.keyUuid) state.pendingAddressListsForKey = false
      else state.pendingAvailableAddressListsForKey = false
    },
    [fetchEntityList.pending]: (state) => {
      state.pendingList = true
    },
    [fetchEntityList.fulfilled]: (state, { payload }) => {
      state.pendingList = false
      state.currentList = payload
    },
    [fetchEntityList.rejected]: (state) => {
      state.pendingList = false
    },
    [fetchAvailableEntries.pending]: (state) => {
      state.pendingEntries = true
    },
    [fetchAvailableEntries.fulfilled]: (state, { payload }) => {
      state.pendingEntries = false
      state.availableEntries = payload.data
      const {
        total_count: total = 0,
        total_pages: totalPages = 1,
        page = 1,
        per_page: perPage = 25,
      } = payload.meta
      state.availableEntriesMeta = { total, totalPages, page, perPage }
    },
    [fetchAvailableEntries.rejected]: (state) => {
      state.pendingEntries = false
    },
    [fetchEntriesByEntityList.pending]: (state) => {
      state.pendingEntriesByEntityList = true
    },
    [fetchEntriesByEntityList.fulfilled]: (state, { payload }) => {
      state.pendingEntriesByEntityList = false
      state.entriesByEntityList = payload.data
      const {
        total_count: total = 0,
        total_pages: totalPages = 1,
        page = 1,
        per_page: perPage = 25,
      } = payload.meta
      state.entriesByEntityListMeta = { total, totalPages, page, perPage }
    },
    [fetchEntriesByEntityList.rejected]: (state) => {
      state.pendingEntriesByEntityList = false
    },
    [fetchAllEntriesByEntityList.pending]: (state) => {
      state.pendingAllEntriesByEntityList = true
    },
    [fetchAllEntriesByEntityList.fulfilled]: (state, { payload }) => {
      state.pendingAllEntriesByEntityList = false
      state.allEntriesByEntityList = payload.data
    },
    [fetchAllEntriesByEntityList.rejected]: (state) => {
      state.pendingAllEntriesByEntityList = false
    },
    [syncEntityListEntries.pending]: (state) => {
      state.pendingEntrySync = true
    },
    [syncEntityListEntries.fulfilled]: (state) => {
      state.pendingEntrySync = false
    },
    [syncEntityListEntries.rejected]: (state) => {
      state.pendingEntrySync = false
    },
    [fetchKeyLists.pending]: (state) => {
      state.pendingKeyLists = true
    },
    [fetchKeyLists.fulfilled]: (state, { payload }) => {
      state.pendingKeyLists = false
      state.keyLists = payload.data || []
      const {
        total_count: total = 0,
        page = 1,
        per_page: perPage = 25,
        total_pages: totalPages = 1,
      } = payload.metadata?.meta || {}
      state.keyListsMeta = { total, page, perPage, totalPages }
    },
    [fetchKeyLists.rejected]: (state) => {
      state.pendingKeyLists = false
    },
    [fetchMasterKeysByEntityList.pending]: (state) => {
      state.pendingMasterKeysByEntityList = true
    },
    [fetchMasterKeysByEntityList.fulfilled]: (state, { payload }) => {
      state.pendingMasterKeysByEntityList = false
      state.masterKeysByEntityList = payload.data
      const {
        total_count: total = 0,
        total_pages: totalPages = 1,
        page = 1,
        per_page: perPage = 25,
      } = payload.meta
      state.masterKeysByEntityListMeta = {
        total,
        totalPages,
        page,
        perPage,
      }
    },
    [fetchMasterKeysByEntityList.rejected]: (state) => {
      state.pendingMasterKeysByEntityList = false
    },
    [fetchAllMasterKeysByEntityList.pending]: (state) => {
      state.pendingAllMasterKeysByEntityList = true
    },
    [fetchAllMasterKeysByEntityList.fulfilled]: (state, { payload }) => {
      state.pendingAllMasterKeysByEntityList = false
      state.allMasterKeysByEntityList = payload.data
    },
    [fetchAllMasterKeysByEntityList.rejected]: (state) => {
      state.pendingAllMasterKeysByEntityList = false
    },
    [fetchAvailableMasterKeys.pending]: (state) => {
      state.pendingMasterKeys = true
    },
    [fetchAvailableMasterKeys.fulfilled]: (state, { payload }) => {
      state.pendingMasterKeys = false
      state.availableMasterKeys = payload.data
      const {
        total_count: total = 0,
        total_pages: totalPages = 1,
        page = 1,
        per_page: perPage = 25,
      } = payload.meta
      state.availableMasterKeysMeta = { total, totalPages, page, perPage }
    },
    [fetchAvailableMasterKeys.rejected]: (state) => {
      state.pendingMasterKeys = false
    },
    [syncKeyListMembers.pending]: (state) => {
      state.pendingMasterKeySync = true
    },
    [syncKeyListMembers.fulfilled]: (state) => {
      state.pendingMasterKeySync = false
    },
    [syncKeyListMembers.rejected]: (state) => {
      state.pendingMasterKeySync = false
    },
    [deleteEntityList.pending]: (state) => {
      state.pendingDelete = true
    },
    [deleteEntityList.fulfilled]: (state) => {
      state.pendingDelete = false
    },
    [deleteEntityList.rejected]: (state) => {
      state.pendingDelete = false
    },
  },
})

export const {
  clearAddressLists,
  clearKeyLists,
  clearAddressListsForKey,
  clearKeyListsForKey,
  clearAvailableEntries,
  clearEntriesByEntityList,
  clearAllEntriesByEntityList,
  clearAvailableMasterKeys,
  clearCurrentList,
  clearMasterKeysByEntityList,
  clearAllMasterKeysByEntityList,
} = addressListsSlice.actions
export default addressListsSlice.reducer
